import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { DefaultRoleSummary, DefaultRoleSummaryStore, roleIsLocked } from 'src/stores/default-roles-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { ENV } from 'src/';
import { emptyTableModel, getRoleDisplayName } from 'src/common/utils';
import { useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { Selector, SelectorValue } from 'src/common/types';

const DefaultRolesListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const defaultRolesStore = container.get(DefaultRoleSummaryStore);
  const defaultRolesState = defaultRolesStore.state;

  setHeader(t('DefaultRoles'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'default-roles', breadcrumbName: t('DefaultRoles') },
  ]);

  useEffect(
    () => {
      defaultRolesStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /*   const onDelete = async (item: DefaultRoleSummary) => {
    modal.confirm({
      title: <h3>{`${t('Confirmation')}`}</h3>,
      icon: null,
      onOk: async () => {
        await defaultRolesStore.delete(item.id);
        await defaultRolesStore.load(query);
      },
      content: item.addToScopeTypeCreator == null ? t('DefaultRoleDelete') : t('DefaultRoleDeleteWarning'),
      okText: t('Delete'),
      okButtonProps: { style: { width: 120 } },
      cancelText: t('Cancel'),
      cancelButtonProps: { type: 'default', style: { width: 120 } }
    });
  }; */

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={defaultRolesStore.baseUrl} fieldName={'Name'} fieldValue={'name'} />,
        fiterField: 'Name',
        renderer: (data) => <span>{getRoleDisplayName(data.name)}</span>,
      },
      {
        sortable: true,
        field: 'selector',
        title: t('Selector'),
        filter: <OptionsFilter options={Object.entries(Selector).map(([key, value]) => ({ label: t(key), value: value }))} />,
        renderer: (data) => (
          <span>
            <Tag>{t(data.selector)}</Tag>
          </span>
        ),
      },
      {
        sortable: true,
        field: 'addToScopeTypeCreator',
        title: t('AssignToScopeCreator'),
        filter: (
          <OptionsFilter
            options={Object.entries(Selector)
              .filter(([_, value]) => SelectorValue[value] <= SelectorValue[Selector.Organization])
              .map(([key, value]) => ({ label: t(key), value: value }))}
          />
        ),
        renderer: (data) =>
          data.addToScopeTypeCreator && (
            <span>
              <Tag>{t(data.addToScopeTypeCreator)}</Tag>
            </span>
          ),
      },
      {
        sortable: true,
        field: 'PartnersOnly',
        title: t('PartnersOnly'),
        filter: (
          <OptionsFilter
            options={[
              { label: t('Yes'), value: true },
              { label: t('No'), value: false },
            ]}
          />
        ),

        renderer: (data) => <span>{data.partnersOnly ? t('Yes') : t('No')}</span>,
      },
      {
        renderer: (data: DefaultRoleSummary) =>
          !roleIsLocked(data.name) && (
            <span className="clickable-span strong" onClick={() => navigate(`update/${data.id}`, { state: data })}>
              {t('Edit')}
            </span>
          ),
      },
    ],
    data: defaultRolesState.value,
  } as TableModel<DefaultRoleSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          onNewItem={() => navigate(`new`)}
          newItemName={t('CreateDefaultRole')}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            defaultRolesStore.load(query);
          }}
          onRefresh={() => defaultRolesStore.load(query)}
          model={defaultRolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            defaultRolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(defaultRolesState.errorMessage.value) : t('DefaultRolesTableError'))
          }
        />
      </Card>
    </Row>
  );
};

export default DefaultRolesListPage;
